import { c as _c } from "react/compiler-runtime";
import { trackEvent } from '@svenskaspelab/nova-core/nextjs/analytics';
import { loginRedirect } from '@svenskaspelab/nova-core/nextjs/auth';
import type { Draw, DrawNumber } from '@svenskaspelab/poolbetting/schema/draw';
import type { Definition } from '@svenskaspelab/poolbetting/schema/frameworks';
import type { BettingEvent, BettingEventId, Participant, ParticipantId } from '@svenskaspelab/poolbetting/schema/sport';
import { ShareButton } from '@svenskaspelab/poolbetting/ui/ShareButton';
import { ShowHideButton } from '@svenskaspelab/poolbetting/ui/ShowHideButton';
import Balance from '@svenskaspelab/ui/react/icons/Balance';
import { useMediaQueryAbove } from '@svenskaspelab/ui/react/media-query';
import type { ReactNode } from 'react';
import BetButtonsClientContainer from './BetButtonsClientContainer';
import ParticipantsInfo from './ParticipantsInfo';

/**
 * Props for the SrowCouponOverview component.
 * Contains all necessary data for displaying a sports betting coupon overview.
 */
type SrowCouponOverviewProps = {
  /** The unique identifier for the draw */
  drawNumber: DrawNumber;
  /** Product definition containing draw product configuration */
  product: Definition['drawProducts'][number];
  /** Draw data and configuration */
  draw: Draw;
  /** User's current account balance in SEK */
  balance: number;
  /** Whether the user should be redirected to login when accessing balance */
  shouldRedirectUser: boolean;
  /** URL for sharing the coupon */
  url: string;
  /** Whether the share functionality is currently disabled */
  isDisabled: boolean;
  /** Total price of the betting coupon in SEK */
  price: number;
  /** Total number of betting rows/lines in the coupon */
  nrOfRows: number;
  /** Record mapping participant IDs to participant data */
  drawParticipants: Record<ParticipantId, Participant>;
  /** Record mapping betting event IDs to betting event data */
  bettingEvents: Record<BettingEventId, BettingEvent>;
};

/**
 * SrowCouponOverview displays a comprehensive overview of a sports betting coupon.
 *
 * This component shows participant information, betting options, balance details,
 * and provides functionality for sharing the coupon. It handles responsive design
 * for both mobile and desktop views.
 *
 * @param props - The component props
 * @param props.drawNumber - The unique identifier for the draw
 * @param props.product - Product definition containing draw product configuration
 * @param props.draw - Draw data and configuration
 * @param props.balance - User's current account balance in SEK (defaults to 0)
 * @param props.shouldRedirectUser - Whether to redirect to login when accessing balance
 * @param props.url - URL for sharing the coupon
 * @param props.isDisabled - Whether the share functionality is disabled
 * @param props.price - Total price of the betting coupon in SEK
 * @param props.nrOfRows - Total number of betting rows in the coupon
 * @param props.drawParticipants - Record mapping participant IDs to participant data
 * @param props.bettingEvents - Record mapping betting event IDs to betting event data
 * @returns JSX element representing the coupon overview
 */
export default function SrowCouponOverview(t0) {
  const $ = _c(35);
  const {
    drawNumber,
    product,
    draw,
    balance,
    shouldRedirectUser,
    url,
    isDisabled,
    price,
    nrOfRows,
    drawParticipants,
    bettingEvents
  } = t0;
  const handleBalanceClick = shouldRedirectUser ? _temp : undefined;
  const isLargeScreen = useMediaQueryAbove("md");
  let t1;
  if ($[0] !== bettingEvents || $[1] !== draw || $[2] !== drawNumber || $[3] !== drawParticipants || $[4] !== nrOfRows || $[5] !== product) {
    t1 = <ParticipantsInfo drawNumber={drawNumber} product={product} draw={draw} drawParticipants={drawParticipants} bettingEvents={bettingEvents} nrOfRows={nrOfRows} />;
    $[0] = bettingEvents;
    $[1] = draw;
    $[2] = drawNumber;
    $[3] = drawParticipants;
    $[4] = nrOfRows;
    $[5] = product;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <BetButtonsClientContainer />;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1) {
    t3 = <div className="border-stroke-low grid grid-cols-[1fr_auto] grid-rows-[repeat(9,auto)] rounded-md lg:border lg:pl-2">{t1}{t2}</div>;
    $[8] = t1;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div>Enkelrader</div>;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = `${nrOfRows} st`;
  let t6;
  if ($[11] !== t5) {
    t6 = <div>Rader: <span className="font-[500]">{t5}</span></div>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = `${price} kr`;
  let t8;
  if ($[13] !== t7) {
    t8 = <div className="justify-self-end font-[500]">{t7}</div>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t6 || $[16] !== t8) {
    t9 = <div className="mt-3 hidden grid-flow-col grid-cols-[max-content_max-content_1fr] gap-x-3 text-sm lg:grid">{t4}{t6}{t8}</div>;
    $[15] = t6;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = `${balance} kr`;
  let t11;
  if ($[18] !== handleBalanceClick || $[19] !== t10) {
    t11 = <ShowHideButton className="text-left lg:hidden" showValue={t10} hideValue="Visa saldo" onClick={handleBalanceClick} Icon={Balance} variant="ghost" />;
    $[18] = handleBalanceClick;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = isLargeScreen ? "outline" : "ghost";
  let t13;
  if ($[21] !== url) {
    t13 = () => {
      trackEvent({
        category: "Nya tipsen",
        action: "Dela din kupong",
        optLabel: url
      });
    };
    $[21] = url;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== isDisabled || $[24] !== t12 || $[25] !== t13 || $[26] !== url) {
    t14 = <ShareButton variant={t12} isDisabled={isDisabled} url={url} onClick={t13} />;
    $[23] = isDisabled;
    $[24] = t12;
    $[25] = t13;
    $[26] = url;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t11 || $[29] !== t14) {
    t15 = <div className="mt-3 grid grid-flow-col">{t11}{t14}</div>;
    $[28] = t11;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t15 || $[32] !== t3 || $[33] !== t9) {
    t16 = <div className="text-surface-fg overflow-auto lg:p-6">{t3}{t9}{t15}</div>;
    $[31] = t15;
    $[32] = t3;
    $[33] = t9;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  return t16;
}
function _temp() {
  loginRedirect();
}